import { Directive, ElementRef, inject, input, effect, Renderer2 } from '@angular/core';

@Directive({
  selector: '[appStaggeredSplitText]',
  standalone: true
})
export class StaggeredSplitTextDirective {
  text = input.required<string>();
  textColor = input<string>('text-white'); // default color
  isVisible = input<boolean>(false);

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private charElements: HTMLElement[] = [];

  constructor() {
    effect(() => {
      this.renderText(this.text());
    });

    effect(() => {
      setTimeout(() => {
        this.updateVisibility(this.isVisible());
      }, 100);
    });
  }

  private renderText(text: string) {
    const container = this.el.nativeElement;
    this.renderer.setProperty(container, 'innerHTML', ''); // Clear content
    this.charElements = [];

    const words = text.split(' ');
    
    words.forEach((word, wordIndex) => {
      // Create word wrapper to prevent breaking
      const wordSpan = this.renderer.createElement('span');
      this.renderer.addClass(wordSpan, 'inline-block');
      this.renderer.addClass(wordSpan, 'whitespace-nowrap');
      
      // Add space before word if not first word
      if (wordIndex > 0) {
        this.renderer.appendChild(container, this.renderer.createText(' ')); 
      }

      const chars = word.split('');
      chars.forEach((char, charIndex) => {
        const charSpan = this.renderer.createElement('span');
        this.renderer.setProperty(charSpan, 'textContent', char);
        
        // Base classes for animation
        const baseClasses = ['inline-block', 'transition-all', 'duration-700', 'ease-out'];
        baseClasses.forEach(c => this.renderer.addClass(charSpan, c));
        
        // Add color class
        if (this.textColor()) {
            this.renderer.addClass(charSpan, this.textColor()); 
        }

        // Initial state (fully hidden and translated)
        this.renderer.addClass(charSpan, 'opacity-0');
        this.renderer.addClass(charSpan, '-translate-y-8');
        
        this.renderer.appendChild(wordSpan, charSpan);
        this.charElements.push(charSpan);
      });

      this.renderer.appendChild(container, wordSpan);
    });

    // Re-apply visibility. If isVisible is ALREADY true, we need to ensure the browser paints the 'hidden' state first
    // so the transition happens.
    if (this.isVisible()) {
       // Force a small delay to allow browser to register the initial state
       setTimeout(() => {
          this.updateVisibility(true);
       }, 50);
    } else {
        this.updateVisibility(false);
    }
  }

  private updateVisibility(visible: boolean) {
    this.charElements.forEach((el, i) => {
      // Set delay
      if (visible) {
        this.renderer.removeClass(el, 'opacity-0');
        this.renderer.addClass(el, 'opacity-100');
        this.renderer.removeClass(el, '-translate-y-8');
        this.renderer.addClass(el, 'translate-y-0');
        this.renderer.setStyle(el, 'transition-delay', `${i * 30}ms`);
      } else {
        this.renderer.removeClass(el, 'opacity-100');
        this.renderer.addClass(el, 'opacity-0');
        this.renderer.removeClass(el, 'translate-y-0');
        this.renderer.addClass(el, '-translate-y-8');
      }
    });
  }
}
